import { cn } from "@/lib/utils"
import type { DataClassification, FeasibilityFlag, PriorityBand, UseCaseStatus } from "@/lib/types"

interface BadgeProps {
  className?: string
}

export function ClassificationBadge({
  value,
  className,
}: BadgeProps & { value: DataClassification }) {
  const styles: Record<DataClassification, string> = {
    Public: "bg-emerald-100 text-emerald-800 border-emerald-200",
    L0: "bg-blue-100 text-blue-800 border-blue-200",
    L1: "bg-orange-100 text-orange-800 border-orange-200",
  }
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
        styles[value],
        className
      )}
    >
      {value}
    </span>
  )
}

export function FeasibilityBadge({
  value,
  className,
}: BadgeProps & { value: FeasibilityFlag }) {
  const styles: Record<FeasibilityFlag, string> = {
    Green: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Amber: "bg-amber-100 text-amber-800 border-amber-200",
    Red: "bg-red-100 text-red-800 border-red-200",
  }
  const dots: Record<FeasibilityFlag, string> = {
    Green: "bg-emerald-500",
    Amber: "bg-amber-500",
    Red: "bg-red-500",
  }
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium",
        styles[value],
        className
      )}
    >
      <span className={cn("size-1.5 rounded-full", dots[value])} />
      {value}
    </span>
  )
}

export function PriorityBadge({
  value,
  className,
}: BadgeProps & { value: PriorityBand }) {
  const styles: Record<PriorityBand, string> = {
    P1: "bg-red-100 text-red-800 border-red-200",
    P2: "bg-orange-100 text-orange-800 border-orange-200",
    P3: "bg-yellow-100 text-yellow-800 border-yellow-200",
    P4: "bg-gray-100 text-gray-700 border-gray-200",
  }
  const labels: Record<PriorityBand, string> = {
    P1: "P1 — Start First",
    P2: "P2 — Strong Candidate",
    P3: "P3 — Needs Discovery",
    P4: "P4 — Park for Now",
  }
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
        styles[value],
        className
      )}
    >
      {labels[value]}
    </span>
  )
}

export function StatusBadge({
  value,
  className,
}: BadgeProps & { value: UseCaseStatus }) {
  const styles: Record<UseCaseStatus, string> = {
    Draft: "bg-gray-100 text-gray-700 border-gray-200",
    Submitted: "bg-blue-100 text-blue-800 border-blue-200",
    "AI Mapped": "bg-violet-100 text-violet-800 border-violet-200",
    "Feasibility Pending": "bg-amber-100 text-amber-800 border-amber-200",
    "Feasibility Completed": "bg-cyan-100 text-cyan-800 border-cyan-200",
    Prioritized: "bg-indigo-100 text-indigo-800 border-indigo-200",
    "Approved for Discovery": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "In POC": "bg-teal-100 text-teal-800 border-teal-200",
    "In Delivery": "bg-sky-100 text-sky-800 border-sky-200",
    Live: "bg-green-100 text-green-800 border-green-200",
    Rejected: "bg-red-100 text-red-800 border-red-200",
    Parked: "bg-gray-100 text-gray-600 border-gray-200",
  }
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        styles[value],
        className
      )}
    >
      {value}
    </span>
  )
}
